import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { insightsHeader } from '@maximizer/core/shared/domain';
import {
  catchError,
  map,
  Observable,
  of,
  OperatorFunction,
  throwError,
} from 'rxjs';
import {
  OUTLOOK_SYNC_API_URL,
  OUTLOOK_SYNC_SUBSCRIPTION_KEY,
} from '../config';

export abstract class OutlookSyncHttpBase {
  protected readonly apiUrl: string = inject(OUTLOOK_SYNC_API_URL);
  protected readonly subscriptionKey: string = inject(
    OUTLOOK_SYNC_SUBSCRIPTION_KEY,
  );
  protected readonly http = inject(HttpClient);

  protected get syncConfigBasePath(): string {
    return `${this.apiUrl}/v1/sync-configurations`;
  }

  protected buildHeaders(options?: {
    ignoreInsights?: boolean;
    extra?: Record<string, string>;
  }): Record<string, string> {
    const headers: Record<string, string> = {};

    // Empty key = server-side proxy injects it; sending an empty header value would get rejected by APIM.
    if (this.subscriptionKey) {
      headers['Ocp-Apim-Subscription-Key'] = this.subscriptionKey;
    }

    if (options?.ignoreInsights) {
      headers[insightsHeader('ignore')] = 'true';
    }

    if (options?.extra) {
      Object.assign(headers, options.extra);
    }
    return headers;
  }

  protected safeGet<T>(
    url: string,
    options: {
      headers?: Record<string, string>;
      params?: HttpParams;
      onError?: (err: unknown) => void;
    } = {},
  ): Observable<T | undefined> {
    return this.http
      .get<T>(url, {
        headers: options.headers ?? this.buildHeaders(),
        params: options.params,
      })
      .pipe(
        // Normalize null body → undefined to match the Observable<T|undefined> contract.
        map((value) => (value ?? undefined) as T | undefined),
        catchError((err) => {
          options.onError?.(err);
          if (
            err instanceof HttpErrorResponse &&
            err.status === HttpStatusCode.NotFound
          ) {
            return of(undefined);
          }
          return throwError(() => err);
        }),
      );
  }

  protected mapStatusOk<T>(
    options: {
      allowed?: HttpStatusCode[];
      catchAs?: boolean;
    } = {},
  ): OperatorFunction<HttpResponse<T>, boolean> {
    return (source) => {
      const mapped = source.pipe(
        map((response) =>
          options.allowed && options.allowed.length > 0
            ? options.allowed.includes(response.status)
            : response.ok,
        ),
      );
      return options.catchAs !== undefined
        ? mapped.pipe(catchError(() => of(options.catchAs as boolean)))
        : mapped;
    };
  }
}
